// ─── Asset Detail Extras ───
// Keys use composite zone-based ID format: "{zonePrefix}-{numericId}"
// Fallback also supports legacy assetId format (A231, A232, etc.)
// Sensor values here MUST match dashboardMockData & assetsMockData exactly.

const buildExtras = ({
  sensors,
  logsAndEvents,
  riskAlertHistory,
  serviceHistory,
  workOrders,
  comments,
}) => ({
  connectedSensors: sensors,
  logsAndEvents,
  riskAlertHistory,
  serviceHistory,
  workOrders,
  comments,
});

// ─── Finishing Zone Asset 1: CNC Lathe (A231) ───
const cncLatheExtras = buildExtras({
  sensors: {
    reportedTimestamp: 'Feb 19, 2026, 10:32 AM',
    items: [
      { id: 'temperature', label: 'Temperature', value: 95, unit: '°C', status: 'Critical', normalRange: '20-60 °C', criticalThreshold: '> 80 °C' },
      { id: 'vibration', label: 'Vibration level', value: 6.5, unit: 'mm/s', status: 'Critical', normalRange: '0-3', criticalThreshold: '> 6' },
      { id: 'pressure', label: 'Pressure', value: 2.8, unit: 'bar', status: 'Healthy', normalRange: '2.5-3.2', criticalThreshold: '> 4' },
    ],
  },
  logsAndEvents: {
    workflow: { label: 'ML Prediction', flowId: 'FLOW-ML-PUMP-001' },
    reasoning: {
      title: 'Complete Decision Reasoning',
      dataInputs: [
        'Vibration sensor time-series (24h)',
        'Temperature logs',
        'Pressure readings',
        'Historical failure records (3 incidents)',
      ],
      analysisFindings:
        'ML anomaly detection flagged 15 consecutive readings beyond 2-sigma. Model trained on 500+ similar pump failures predicts bearing degradation pattern.',
    },
    prediction: { label: 'ML Prediction - Bearing Failure', confidence: 94 },
    recommendation: { label: 'Recommendation - Escalate to Contextual Validation Agent for verification', confidence: 100 },
    steps: [
      {
        stepNumber: 4, title: 'Human Approval', subtitle: 'Work Order Approval & Technician Assignment',
        status: 'Approved', statusColor: '#399432', confidence: 100, date: 'Feb 3, 2026, 10:15 PM',
        description: 'Supervisor reviewed draft work order. Approved bearing replacement with comment: "High-risk asset, proceed immediately. Assign to John Smith (Senior Tech)." Work order WO-2024-001 officially created in CMMS. Technician John Smith assigned. Scheduling confirmed for 2026-02-04 (start of next shift). Parts confirmed available for immediate fulfillment.',
      },
      {
        stepNumber: 3, title: 'Knowledge Base Lookup & Work Order Planning', subtitle: 'Knowledge Base Lookup & Work Order Planning',
        status: 'Awaiting Approval', statusColor: '#F59E0B', confidence: 91, date: 'Feb 3, 2026, 09:35 PM',
        description: 'Used Knowledge Base to retrieve bearing replacement SOP for CNC Lathe. Identified required parts: Bearing Assembly, Seal Kit, Lubricant. Estimated labor: 3.5 hours at senior technician rate. Total estimated cost: $2,450. Spare parts inventory check: All parts in stock (sufficient quantity). Created draft work order WO-2024-001 with Goal-Weighted priority assessment: High priority due to imminent failure risk (RUL < 3 weeks) and critical system importance.',
      },
      {
        stepNumber: 2, title: 'Contextual Validation', subtitle: 'Verify Machine Logs & Maintenance History',
        status: 'Validated', statusColor: '#3B82F6', confidence: 94, date: 'Feb 3, 2026, 08:48 PM',
        description: 'Verified sensor anomaly against machine logs and maintenance records. Machine diagnostics confirm bearing wear pattern. CMMS data shows 2 previous bearing replacements (2021, 2023) with intervals of 18-24 months. Current operating duration since last bearing service: 22 months. Asset failure with bearing degradation cycle. Anomaly is confirmed as genuine mechanical issue, not sensor malfunction.',
      },
      {
        stepNumber: 1, title: 'Anomaly Trigger', subtitle: 'Sensor Data Analysis & ML Model Prediction',
        status: 'Escalated', statusColor: '#8B5CF6', confidence: 89, date: 'Feb 3, 2026, 07:52 PM',
        description: 'Sensor Data Tool detected abnormal vibration pattern. ML Model Tool analysis predicts bearing failure with 94% confidence. Vibration increased from baseline 2.5 mm/s to current 6.5 mm/s. Based on historical data of 3 previous failures on this asset, similar patterns preceded failures by 14-21 days.',
        detailedAnalysis: 'Sensor Data Tool detected abnormal vibration pattern. ML Model Tool analysis predicts bearing failure with 94% confidence. Vibration increased from baseline 2.5 mm/s to current 6.5 mm/s. Based on historical data of 3 previous failures on this asset, similar patterns preceded failures by 14-21 days.',
        agentInfo: { agentName: 'Predictive Agent', version: 'V 2.1.4', workflow: 'Automated Workflow', riskScore: 82, confidence: 94, nextAgent: 'Contextual Validation Agent' },
      },
    ],
  },
  riskAlertHistory: [
    { id: 1, title: 'Bearing temperature exceeded critical threshold (95°C vs 60°C limit)', timeAgo: '2 minutes ago', severity: 'critical' },
    { id: 2, title: 'Vibration level trending upward - predictive model suggests failure risk', timeAgo: '45 minutes ago', severity: 'warning' },
    { id: 3, title: 'High bearing vibration detected (6.5 mm/s)', timeAgo: '8 hours ago', severity: 'warning' },
    { id: 4, title: 'Routine maintenance reminder - Last oil change: 30 days ago', timeAgo: '1 day ago', severity: 'info' },
  ],
  serviceHistory: [
    { id: 1, title: 'Oil Change', date: 'Jan 15, 2026 by Lisa Cruise', details: 'Standard maintenance, Oil analysis normal.', status: 'Marked as completed by technician', approvedBy: 'Supervisor' },
    { id: 2, title: 'Bearing Replacement', date: 'Oct 20, 2025 by Alex Brown', details: 'Replaced main bearing assembly. Vibration levels normalized.', status: 'Marked as completed by technician', approvedBy: 'Supervisor' },
    { id: 3, title: 'Annual Inspection', date: 'Jul 10, 2025 by Maria Garcia', details: 'Full inspection completed. All systems within tolerance.', status: 'Marked as completed by technician', approvedBy: 'Supervisor' },
  ],
  workOrders: [
    { id: 'WO-2024-001', title: 'Emergency Bearing Replacement', status: 'Scheduled', statusColor: '#3B82F6', priority: 'Critical', priorityColor: '#EF4444', assignedTo: 'Lisa Cruise', dueDate: 'Today by 5 PM' },
    { id: 'WO-2024-002', title: 'Oil Analysis - Pump A12', status: 'Pending', statusColor: '#F59E0B', priority: 'Critical', priorityColor: '#EF4444', assignedTo: 'Jane Cena', dueDate: 'Tomorrow' },
    { id: 'WO-2023-456', title: 'Routine Maintenance', status: 'Completed', statusColor: '#399432', priority: 'Medium', priorityColor: '#F59E0B', assignedTo: 'Brad Sitt', dueDate: 'Jan 15, 2026' },
    { id: 'WO-2023-455', title: 'Vibration Analysis', status: 'Completed', statusColor: '#399432', priority: 'Medium', priorityColor: '#F59E0B', assignedTo: 'Tommy V', dueDate: 'Jan 10, 2026' },
  ],
  comments: [
    { id: 1, author: 'Lisa Cruise', role: 'Technician', avatar: 'LC', timeAgo: '2 days ago', text: 'Recommend immediate bearing replacement. Temperature trends are concerning.' },
    { id: 2, author: 'Tommy V', role: 'Supervisor', avatar: 'TV', timeAgo: '1 day ago', text: 'Approved for emergency maintenance. Please schedule for today if possible.' },
  ],
});

// ─── Finishing Zone Asset 2: Hydraulic Pump (A232) ───
const hydraulicPumpExtras = buildExtras({
  sensors: {
    reportedTimestamp: 'Feb 19, 2026, 10:28 AM',
    items: [
      { id: 'temperature', label: 'Temperature', value: 68, unit: '°C', status: 'Warning', normalRange: '20-60 °C', criticalThreshold: '> 80 °C' },
      { id: 'vibration', label: 'Vibration level', value: 4.2, unit: 'mm/s', status: 'Warning', normalRange: '0-3', criticalThreshold: '> 6' },
      { id: 'pressure', label: 'Pressure', value: 2.3, unit: 'bar', status: 'Warning', normalRange: '2.5-3.2', criticalThreshold: '< 2.0' },
    ],
  },
  logsAndEvents: {
    workflow: { label: 'ML Prediction', flowId: 'FLOW-ML-HYD-002' },
    reasoning: {
      title: 'Complete Decision Reasoning',
      dataInputs: ['Pressure sensor time-series (48h)', 'Temperature logs', 'Vibration readings', 'Oil analysis records'],
      analysisFindings: 'Gradual pressure decline pattern detected. Oil contamination suspected based on temperature correlation with pressure drops.',
    },
    prediction: { label: 'ML Prediction - Hydraulic Seal Degradation', confidence: 85 },
    recommendation: { label: 'Recommendation - Schedule oil change and filter replacement within 2 weeks', confidence: 90 },
    steps: [
      { stepNumber: 2, title: 'Contextual Validation', subtitle: 'Verify Machine Logs', status: 'Validated', statusColor: '#3B82F6', confidence: 88, date: 'Feb 2, 2026, 03:15 PM', description: 'Pressure readings confirmed declining over 48 hours. Oil analysis from last month showed elevated particulate count.' },
      { stepNumber: 1, title: 'Anomaly Trigger', subtitle: 'Sensor Data Analysis', status: 'Escalated', statusColor: '#8B5CF6', confidence: 85, date: 'Feb 2, 2026, 02:30 PM', description: 'Pressure dropped below normal operating range. Temperature elevated 13% above baseline.', agentInfo: { agentName: 'Predictive Agent', version: 'V 2.1.4', workflow: 'Automated Workflow', riskScore: 65, confidence: 85, nextAgent: 'Contextual Validation Agent' } },
    ],
  },
  riskAlertHistory: [
    { id: 1, title: 'Pressure below normal range (2.3 bar vs 2.5 bar minimum)', timeAgo: '5 minutes ago', severity: 'warning' },
    { id: 2, title: 'Temperature trending upward (68°C vs 60°C normal max)', timeAgo: '1 hour ago', severity: 'warning' },
    { id: 3, title: 'Oil analysis recommended based on operating hours', timeAgo: '2 days ago', severity: 'info' },
  ],
  serviceHistory: [
    { id: 1, title: 'Oil Change & Filter Replacement', date: 'Dec 20, 2025 by Alex Brown', details: 'Routine oil change completed. Filter replaced.', status: 'Marked as completed by technician', approvedBy: 'Supervisor' },
    { id: 2, title: 'Seal Inspection', date: 'Sep 15, 2025 by Maria Garcia', details: 'Visual inspection of hydraulic seals. Minor wear noted.', status: 'Marked as completed by technician', approvedBy: 'Supervisor' },
  ],
  workOrders: [
    { id: 'WO-2024-006', title: 'Scheduled Oil Change', status: 'Pending', statusColor: '#F59E0B', priority: 'High', priorityColor: '#EF4444', assignedTo: 'Alex Brown', dueDate: 'Feb 25, 2026' },
    { id: 'WO-2023-450', title: 'Annual Pump Inspection', status: 'Completed', statusColor: '#399432', priority: 'Medium', priorityColor: '#F59E0B', assignedTo: 'David Lee', dueDate: 'Dec 20, 2025' },
  ],
  comments: [
    { id: 1, author: 'Alex Brown', role: 'Technician', avatar: 'AB', timeAgo: '3 days ago', text: 'Pressure readings have been slowly declining. Recommend scheduling oil change and seal inspection soon.' },
  ],
});

// ─── Finishing Zone Asset 3: Main Drive Motor (A233) ───
const mainDriveMotorExtras = buildExtras({
  sensors: {
    reportedTimestamp: 'Feb 19, 2026, 10:25 AM',
    items: [
      { id: 'temperature', label: 'Temperature', value: 80, unit: '°C', status: 'Warning', normalRange: '20-60 °C', criticalThreshold: '> 80 °C' },
      { id: 'vibration', label: 'Vibration level', value: 4.2, unit: 'mm/s', status: 'Warning', normalRange: '0-3', criticalThreshold: '> 6' },
      { id: 'pressure', label: 'Pressure', value: 2.9, unit: 'bar', status: 'Healthy', normalRange: '2.5-3.2', criticalThreshold: '> 4' },
    ],
  },
  logsAndEvents: {
    workflow: { label: 'ML Prediction', flowId: 'FLOW-ML-MTR-003' },
    reasoning: {
      title: 'Complete Decision Reasoning',
      dataInputs: ['Temperature sensor time-series (72h)', 'Vibration readings', 'Motor current logs'],
      analysisFindings: 'Temperature trending upward over past 72 hours. Vibration slightly elevated but stable. Monitoring recommended.',
    },
    prediction: { label: 'ML Prediction - Potential Overheating', confidence: 75 },
    recommendation: { label: 'Recommendation - Continue monitoring, next inspection in 30 days', confidence: 85 },
    steps: [
      { stepNumber: 1, title: 'Anomaly Trigger', subtitle: 'Temperature Trend Analysis', status: 'Monitoring', statusColor: '#F59E0B', confidence: 75, date: 'Feb 1, 2026, 11:00 AM', description: 'Temperature readings trending upward. Current 80°C is at the critical threshold but stable. Continued monitoring recommended.', agentInfo: { agentName: 'Predictive Agent', version: 'V 2.1.4', workflow: 'Automated Workflow', riskScore: 45, confidence: 75, nextAgent: 'Monitoring Agent' } },
    ],
  },
  riskAlertHistory: [
    { id: 1, title: 'Temperature at critical threshold (80°C)', timeAgo: '8 minutes ago', severity: 'warning' },
    { id: 2, title: 'Vibration slightly above normal range (4.2 mm/s)', timeAgo: '30 minutes ago', severity: 'info' },
    { id: 3, title: 'Routine inspection completed - all within tolerance', timeAgo: '3 days ago', severity: 'info' },
  ],
  serviceHistory: [
    { id: 1, title: 'Vibration Analysis', date: 'Jan 28, 2026 by Tommy V', details: 'Completed vibration analysis. Temperature readings trending upward, monitoring recommended.', status: 'Marked as completed by technician', approvedBy: 'Supervisor' },
    { id: 2, title: 'Motor Winding Test', date: 'Nov 10, 2025 by David Lee', details: 'Insulation resistance test passed. All phases within specification.', status: 'Marked as completed by technician', approvedBy: 'Supervisor' },
  ],
  workOrders: [
    { id: 'WO-2024-004', title: 'Motor Vibration Analysis', status: 'Completed', statusColor: '#399432', priority: 'Medium', priorityColor: '#F59E0B', assignedTo: 'Tommy V', dueDate: 'Jan 28, 2026' },
    { id: 'WO-2024-007', title: 'Routine Inspection', status: 'Scheduled', statusColor: '#3B82F6', priority: 'Low', priorityColor: '#399432', assignedTo: 'David Lee', dueDate: 'Mar 20, 2026' },
  ],
  comments: [
    { id: 1, author: 'Tommy V', role: 'Technician', avatar: 'TV', timeAgo: '5 days ago', text: 'Temperature trending up but stable. Will continue monitoring over next 2 weeks.' },
  ],
});

// ─── Assembly Zone Asset 1: Welding Robot (A234) ───
const weldingRobotExtras = buildExtras({
  sensors: {
    reportedTimestamp: 'Feb 19, 2026, 10:30 AM',
    items: [
      { id: 'temperature', label: 'Temperature', value: 72, unit: '°C', status: 'Warning', normalRange: '20-65 °C', criticalThreshold: '> 80 °C' },
      { id: 'vibration', label: 'Vibration level', value: 5.8, unit: 'mm/s', status: 'Critical', normalRange: '0-3', criticalThreshold: '> 5' },
      { id: 'pressure', label: 'Pressure', value: 3.1, unit: 'bar', status: 'Healthy', normalRange: '2.5-3.2', criticalThreshold: '> 4' },
    ],
  },
  logsAndEvents: {
    workflow: { label: 'ML Prediction', flowId: 'FLOW-ML-ROB-004' },
    reasoning: {
      title: 'Complete Decision Reasoning',
      dataInputs: ['Calibration drift logs', 'Vibration sensor data', 'Assembly defect rate data', 'Historical calibration records'],
      analysisFindings: 'Calibration has drifted 0.8mm beyond tolerance. Assembly defect rate increased 12% this week. Vibration pattern indicates mechanical misalignment in joint 3.',
    },
    prediction: { label: 'ML Prediction - Calibration Failure', confidence: 92 },
    recommendation: { label: 'Recommendation - Emergency recalibration required', confidence: 95 },
    steps: [
      { stepNumber: 3, title: 'Work Order Planning', subtitle: 'Parts & Labor Assessment', status: 'Awaiting Approval', statusColor: '#F59E0B', confidence: 92, date: 'Feb 18, 2026, 04:00 PM', description: 'Calibration module and servo motor identified as required parts. Estimated labor: 4 hours. Total cost: $3,200. Work order WO-2024-010 created.' },
      { stepNumber: 2, title: 'Contextual Validation', subtitle: 'Verify Calibration Logs', status: 'Validated', statusColor: '#3B82F6', confidence: 94, date: 'Feb 18, 2026, 03:15 PM', description: 'Calibration drift confirmed at 0.8mm. Assembly defect data correlates with drift timeline. Mechanical root cause confirmed.' },
      { stepNumber: 1, title: 'Anomaly Trigger', subtitle: 'Calibration Drift Detection', status: 'Escalated', statusColor: '#8B5CF6', confidence: 92, date: 'Feb 18, 2026, 02:30 PM', description: 'Calibration monitoring system detected 0.8mm drift beyond tolerance. Vibration increased significantly in joint 3.', agentInfo: { agentName: 'Predictive Agent', version: 'V 2.1.4', workflow: 'Automated Workflow', riskScore: 88, confidence: 92, nextAgent: 'Contextual Validation Agent' } },
    ],
  },
  riskAlertHistory: [
    { id: 1, title: 'Calibration drift exceeded tolerance (0.8mm vs 0.3mm limit)', timeAgo: '3 minutes ago', severity: 'critical' },
    { id: 2, title: 'Assembly defect rate increased 12% this week', timeAgo: '2 hours ago', severity: 'critical' },
    { id: 3, title: 'Vibration in joint 3 above normal (5.8 mm/s)', timeAgo: '6 hours ago', severity: 'warning' },
  ],
  serviceHistory: [
    { id: 1, title: 'Full Calibration', date: 'Dec 5, 2025 by Lisa Cruise', details: 'Complete recalibration performed. All axes within 0.1mm tolerance.', status: 'Marked as completed by technician', approvedBy: 'Supervisor' },
    { id: 2, title: 'Joint Lubrication', date: 'Oct 15, 2025 by Emma Wilson', details: 'All 6 joints lubricated per maintenance schedule.', status: 'Marked as completed by technician', approvedBy: 'Supervisor' },
  ],
  workOrders: [
    { id: 'WO-2024-010', title: 'Welding Robot Recalibration', status: 'Scheduled', statusColor: '#3B82F6', priority: 'Critical', priorityColor: '#EF4444', assignedTo: 'Lisa Cruise', dueDate: 'Feb 20, 2026' },
    { id: 'WO-2023-460', title: 'Joint Lubrication', status: 'Completed', statusColor: '#399432', priority: 'Low', priorityColor: '#399432', assignedTo: 'Emma Wilson', dueDate: 'Oct 15, 2025' },
  ],
  comments: [
    { id: 1, author: 'Lisa Cruise', role: 'Technician', avatar: 'LC', timeAgo: '1 day ago', text: 'Calibration drift is accelerating. Need to recalibrate ASAP before defect rate worsens.' },
    { id: 2, author: 'John Smith', role: 'Supervisor', avatar: 'JS', timeAgo: '12 hours ago', text: 'Approved emergency recalibration. Parts ordered and should arrive today.' },
  ],
});

// ─── Assembly Zone Asset 2: Torque Station (A235) ───
const torqueStationExtras = buildExtras({
  sensors: {
    reportedTimestamp: 'Feb 19, 2026, 10:27 AM',
    items: [
      { id: 'temperature', label: 'Temperature', value: 52, unit: '°C', status: 'Healthy', normalRange: '20-60 °C', criticalThreshold: '> 80 °C' },
      { id: 'vibration', label: 'Vibration level', value: 2.1, unit: 'mm/s', status: 'Healthy', normalRange: '0-3', criticalThreshold: '> 5' },
      { id: 'pressure', label: 'Pressure', value: 2.7, unit: 'bar', status: 'Healthy', normalRange: '2.5-3.2', criticalThreshold: '> 4' },
    ],
  },
  logsAndEvents: {
    workflow: { label: 'ML Prediction', flowId: 'FLOW-ML-TRQ-005' },
    reasoning: {
      title: 'Complete Decision Reasoning',
      dataInputs: ['Torque output logs (500 cycles)', 'Sensor variance data', 'Historical torque calibration records'],
      analysisFindings: '15% variance in torque output detected over last 500 cycles. Sensor physical readings are normal but torque controller showing inconsistent values.',
    },
    prediction: { label: 'ML Prediction - Torque Sensor Degradation', confidence: 88 },
    recommendation: { label: 'Recommendation - Replace torque sensor within 1 week', confidence: 90 },
    steps: [
      { stepNumber: 1, title: 'Anomaly Trigger', subtitle: 'Torque Variance Detection', status: 'Escalated', statusColor: '#8B5CF6', confidence: 88, date: 'Feb 17, 2026, 09:00 AM', description: '15% variance in torque values detected over 500 cycles. Pattern consistent with sensor degradation rather than mechanical failure.', agentInfo: { agentName: 'Predictive Agent', version: 'V 2.1.4', workflow: 'Automated Workflow', riskScore: 55, confidence: 88, nextAgent: 'Contextual Validation Agent' } },
    ],
  },
  riskAlertHistory: [
    { id: 1, title: '15% torque variance detected in last 500 cycles', timeAgo: '6 minutes ago', severity: 'warning' },
    { id: 2, title: 'Torque output trending inconsistent', timeAgo: '4 hours ago', severity: 'info' },
  ],
  serviceHistory: [
    { id: 1, title: 'Torque Calibration', date: 'Jan 5, 2026 by Jane Cena', details: 'Full torque calibration performed. Values within spec at time of service.', status: 'Marked as completed by technician', approvedBy: 'Supervisor' },
  ],
  workOrders: [
    { id: 'WO-2024-011', title: 'Torque Sensor Replacement', status: 'Pending', statusColor: '#F59E0B', priority: 'Medium', priorityColor: '#F59E0B', assignedTo: 'Jane Cena', dueDate: 'Feb 22, 2026' },
  ],
  comments: [
    { id: 1, author: 'Jane Cena', role: 'Technician', avatar: 'JC', timeAgo: '2 days ago', text: 'Torque variance is within acceptable limits for now but sensor replacement should be scheduled soon.' },
  ],
});

// ─── Assembly Zone Asset 3: Assembly Conveyor (A236) ───
const assemblyConveyorExtras = buildExtras({
  sensors: {
    reportedTimestamp: 'Feb 19, 2026, 10:20 AM',
    items: [
      { id: 'temperature', label: 'Temperature', value: 45, unit: '°C', status: 'Healthy', normalRange: '20-60 °C', criticalThreshold: '> 80 °C' },
      { id: 'vibration', label: 'Vibration level', value: 1.8, unit: 'mm/s', status: 'Healthy', normalRange: '0-3', criticalThreshold: '> 5' },
      { id: 'pressure', label: 'Pressure', value: 2.9, unit: 'bar', status: 'Healthy', normalRange: '2.5-3.2', criticalThreshold: '> 4' },
    ],
  },
  logsAndEvents: {
    workflow: { label: 'ML Prediction', flowId: 'FLOW-ML-CNV-006' },
    reasoning: {
      title: 'Complete Decision Reasoning',
      dataInputs: ['Belt tension sensor data', 'Motor current logs', 'Vibration readings'],
      analysisFindings: 'Belt tension gradually decreasing. Predictive model suggests replacement needed within 60 days. All other parameters normal.',
    },
    prediction: { label: 'ML Prediction - Belt Wear', confidence: 75 },
    recommendation: { label: 'Recommendation - Order replacement belt and schedule downtime', confidence: 80 },
    steps: [
      { stepNumber: 1, title: 'Anomaly Trigger', subtitle: 'Belt Tension Monitoring', status: 'Monitoring', statusColor: '#F59E0B', confidence: 75, date: 'Feb 15, 2026, 02:00 PM', description: 'Belt tension decreasing gradually over past 2 weeks. Rate of decline suggests 60 days before operational threshold is reached.', agentInfo: { agentName: 'Predictive Agent', version: 'V 2.1.4', workflow: 'Automated Workflow', riskScore: 30, confidence: 75, nextAgent: 'Monitoring Agent' } },
    ],
  },
  riskAlertHistory: [
    { id: 1, title: 'Belt tension decreasing - scheduled replacement recommended', timeAgo: '12 minutes ago', severity: 'info' },
    { id: 2, title: 'All sensor readings within normal range', timeAgo: '1 hour ago', severity: 'info' },
  ],
  serviceHistory: [
    { id: 1, title: 'Belt Replacement', date: 'Aug 10, 2025 by Brad Sitt', details: 'Conveyor belt replaced. Tension set to manufacturer specification.', status: 'Marked as completed by technician', approvedBy: 'Supervisor' },
    { id: 2, title: 'Roller Lubrication', date: 'Jan 20, 2026 by David Lee', details: 'All rollers lubricated. No abnormal wear detected.', status: 'Marked as completed by technician', approvedBy: 'Supervisor' },
  ],
  workOrders: [
    { id: 'WO-2024-012', title: 'Conveyor Belt Tension Adjustment', status: 'Scheduled', statusColor: '#3B82F6', priority: 'Low', priorityColor: '#399432', assignedTo: 'Brad Sitt', dueDate: 'Mar 1, 2026' },
  ],
  comments: [
    { id: 1, author: 'Brad Sitt', role: 'Technician', avatar: 'BS', timeAgo: '4 days ago', text: 'Belt tension is declining but still within operational range. Replacement belt ordered.' },
  ],
});

// ─── Packaging Zone Asset 1: Heat Sealer (A237) ───
const heatSealerExtras = buildExtras({
  sensors: {
    reportedTimestamp: 'Feb 19, 2026, 10:35 AM',
    items: [
      { id: 'temperature', label: 'Temperature', value: 105, unit: '°C', status: 'Critical', normalRange: '60-90 °C', criticalThreshold: '> 95 °C' },
      { id: 'vibration', label: 'Vibration level', value: 3.5, unit: 'mm/s', status: 'Warning', normalRange: '0-3', criticalThreshold: '> 5' },
      { id: 'pressure', label: 'Pressure', value: 3.4, unit: 'bar', status: 'Warning', normalRange: '2.5-3.2', criticalThreshold: '> 4' },
    ],
  },
  logsAndEvents: {
    workflow: { label: 'ML Prediction', flowId: 'FLOW-ML-HTR-007' },
    reasoning: {
      title: 'Complete Decision Reasoning',
      dataInputs: ['Temperature sensor time-series (24h)', 'Heating element current draw', 'Seal quality metrics', 'Fire safety logs'],
      analysisFindings: 'Heat sealer running 15°C above normal operating temperature. Heating element likely degraded. Risk of seal quality failure and potential fire hazard.',
    },
    prediction: { label: 'ML Prediction - Heating Element Failure', confidence: 95 },
    recommendation: { label: 'Recommendation - Shut down and inspect heating element immediately', confidence: 98 },
    steps: [
      { stepNumber: 2, title: 'Contextual Validation', subtitle: 'Safety Assessment', status: 'Validated', statusColor: '#EF4444', confidence: 96, date: 'Feb 19, 2026, 09:00 AM', description: 'Temperature confirmed 15°C above normal. Heating element current draw elevated. Fire safety protocol initiated. Immediate shutdown recommended.' },
      { stepNumber: 1, title: 'Anomaly Trigger', subtitle: 'Temperature Spike Detection', status: 'Escalated', statusColor: '#8B5CF6', confidence: 95, date: 'Feb 19, 2026, 08:30 AM', description: 'Temperature exceeded safe operating range. Rate of increase indicates heating element thermostat malfunction.', agentInfo: { agentName: 'Predictive Agent', version: 'V 2.1.4', workflow: 'Automated Workflow', riskScore: 95, confidence: 95, nextAgent: 'Safety Validation Agent' } },
    ],
  },
  riskAlertHistory: [
    { id: 1, title: 'Temperature exceeded critical threshold (105°C vs 90°C limit)', timeAgo: '1 minute ago', severity: 'critical' },
    { id: 2, title: 'Fire hazard warning - heating element overheating', timeAgo: '15 minutes ago', severity: 'critical' },
    { id: 3, title: 'Seal quality declining - 8% rejection rate', timeAgo: '2 hours ago', severity: 'warning' },
  ],
  serviceHistory: [
    { id: 1, title: 'Heating Element Inspection', date: 'Jan 10, 2026 by Brad Sitt', details: 'Visual inspection completed. Element showed early signs of wear.', status: 'Marked as completed by technician', approvedBy: 'Supervisor' },
    { id: 2, title: 'Thermostat Calibration', date: 'Nov 5, 2025 by Maria Garcia', details: 'Thermostat recalibrated to factory specifications.', status: 'Marked as completed by technician', approvedBy: 'Supervisor' },
  ],
  workOrders: [
    { id: 'WO-2024-020', title: 'Heat Sealer Element Replacement', status: 'Scheduled', statusColor: '#3B82F6', priority: 'Critical', priorityColor: '#EF4444', assignedTo: 'Brad Sitt', dueDate: 'Feb 18, 2026' },
  ],
  comments: [
    { id: 1, author: 'Brad Sitt', role: 'Technician', avatar: 'BS', timeAgo: '1 day ago', text: 'Heating element is degraded. Ordered replacement parts. Asset should be offline until repair.' },
    { id: 2, author: 'John Smith', role: 'Supervisor', avatar: 'JS', timeAgo: '12 hours ago', text: 'Confirmed shutdown. Safety team notified. Prioritize this repair.' },
  ],
});

// ─── Packaging Zone Asset 2: Label Printer (A238) ───
const labelPrinterExtras = buildExtras({
  sensors: {
    reportedTimestamp: 'Feb 19, 2026, 10:22 AM',
    items: [
      { id: 'temperature', label: 'Temperature', value: 55, unit: '°C', status: 'Healthy', normalRange: '20-60 °C', criticalThreshold: '> 80 °C' },
      { id: 'vibration', label: 'Vibration level', value: 2.5, unit: 'mm/s', status: 'Healthy', normalRange: '0-3', criticalThreshold: '> 5' },
      { id: 'pressure', label: 'Pressure', value: 2.6, unit: 'bar', status: 'Healthy', normalRange: '2.5-3.2', criticalThreshold: '> 4' },
    ],
  },
  logsAndEvents: {
    workflow: { label: 'ML Prediction', flowId: 'FLOW-ML-PRT-008' },
    reasoning: {
      title: 'Complete Decision Reasoning',
      dataInputs: ['Label placement accuracy logs', 'Print head position data', 'Vibration readings'],
      analysisFindings: '8% of labels placed outside tolerance in last batch. Print head alignment has drifted. Mechanical adjustment required.',
    },
    prediction: { label: 'ML Prediction - Print Head Misalignment', confidence: 82 },
    recommendation: { label: 'Recommendation - Realign print head within 2 days', confidence: 88 },
    steps: [
      { stepNumber: 1, title: 'Anomaly Trigger', subtitle: 'Label Accuracy Monitoring', status: 'Escalated', statusColor: '#8B5CF6', confidence: 82, date: 'Feb 18, 2026, 11:00 AM', description: 'Label placement accuracy dropped below 92% threshold. Print head position data shows 0.5mm drift from baseline.', agentInfo: { agentName: 'Predictive Agent', version: 'V 2.1.4', workflow: 'Automated Workflow', riskScore: 50, confidence: 82, nextAgent: 'Contextual Validation Agent' } },
    ],
  },
  riskAlertHistory: [
    { id: 1, title: '8% label misalignment detected in last batch', timeAgo: '10 minutes ago', severity: 'warning' },
    { id: 2, title: 'Print head position drifting from baseline', timeAgo: '3 hours ago', severity: 'info' },
  ],
  serviceHistory: [
    { id: 1, title: 'Print Head Alignment', date: 'Jan 20, 2026 by Lisa Cruise', details: 'Print head realigned. Label accuracy restored to 99.2%.', status: 'Marked as completed by technician', approvedBy: 'Supervisor' },
  ],
  workOrders: [
    { id: 'WO-2024-021', title: 'Label Printer Head Realignment', status: 'Pending', statusColor: '#F59E0B', priority: 'Medium', priorityColor: '#F59E0B', assignedTo: 'Lisa Cruise', dueDate: 'Feb 21, 2026' },
  ],
  comments: [
    { id: 1, author: 'Lisa Cruise', role: 'Technician', avatar: 'LC', timeAgo: '1 day ago', text: 'Print head needs realignment. Can do it during next scheduled downtime.' },
  ],
});

// ─── Packaging Zone Asset 3: Palletizer (A239) ───
const palletizerExtras = buildExtras({
  sensors: {
    reportedTimestamp: 'Feb 19, 2026, 10:18 AM',
    items: [
      { id: 'temperature', label: 'Temperature', value: 58, unit: '°C', status: 'Healthy', normalRange: '20-60 °C', criticalThreshold: '> 80 °C' },
      { id: 'vibration', label: 'Vibration level', value: 3.8, unit: 'mm/s', status: 'Warning', normalRange: '0-3', criticalThreshold: '> 5' },
      { id: 'pressure', label: 'Pressure', value: 2.2, unit: 'bar', status: 'Warning', normalRange: '2.5-3.2', criticalThreshold: '< 2.0' },
    ],
  },
  logsAndEvents: {
    workflow: { label: 'ML Prediction', flowId: 'FLOW-ML-PLT-009' },
    reasoning: {
      title: 'Complete Decision Reasoning',
      dataInputs: ['Hydraulic pressure sensor data', 'Vibration readings', 'Fluid level logs'],
      analysisFindings: 'Hydraulic pressure showing gradual decline. Pattern consistent with seal degradation. Vibration slightly elevated due to compensatory pressure adjustments.',
    },
    prediction: { label: 'ML Prediction - Hydraulic Seal Failure', confidence: 78 },
    recommendation: { label: 'Recommendation - Schedule hydraulic seal replacement', confidence: 85 },
    steps: [
      { stepNumber: 1, title: 'Anomaly Trigger', subtitle: 'Pressure Decline Detection', status: 'Escalated', statusColor: '#8B5CF6', confidence: 78, date: 'Feb 16, 2026, 03:00 PM', description: 'Hydraulic pressure declined 12% over past 2 weeks. Rate of decline suggests seal replacement needed within 30 days.', agentInfo: { agentName: 'Predictive Agent', version: 'V 2.1.4', workflow: 'Automated Workflow', riskScore: 55, confidence: 78, nextAgent: 'Contextual Validation Agent' } },
    ],
  },
  riskAlertHistory: [
    { id: 1, title: 'Hydraulic pressure below normal range (2.2 bar vs 2.5 bar minimum)', timeAgo: '15 minutes ago', severity: 'warning' },
    { id: 2, title: 'Vibration slightly elevated (3.8 mm/s vs 3.0 normal max)', timeAgo: '1 hour ago', severity: 'info' },
  ],
  serviceHistory: [
    { id: 1, title: 'Hydraulic Fluid Change', date: 'Dec 15, 2025 by Jane Cena', details: 'Complete hydraulic fluid replacement. Seals inspected - minor wear noted.', status: 'Marked as completed by technician', approvedBy: 'Supervisor' },
    { id: 2, title: 'Annual Inspection', date: 'Sep 20, 2025 by Maria Garcia', details: 'Full inspection completed. Hydraulic system noted for next scheduled seal replacement.', status: 'Marked as completed by technician', approvedBy: 'Supervisor' },
  ],
  workOrders: [
    { id: 'WO-2024-022', title: 'Palletizer Hydraulic Seal Repair', status: 'Scheduled', statusColor: '#3B82F6', priority: 'High', priorityColor: '#EF4444', assignedTo: 'Jane Cena', dueDate: 'Feb 19, 2026' },
  ],
  comments: [
    { id: 1, author: 'Jane Cena', role: 'Technician', avatar: 'JC', timeAgo: '2 days ago', text: 'Hydraulic pressure declining steadily. Seal kit ordered. Planning repair for this week.' },
  ],
});

// ─── Lookup map ───
// Maps zone-specific asset IDs and legacy assetIds to their extras

export const assetDetailExtras = {
  // Finishing zone (numeric IDs are zone-scoped: asset.id within the zone)
  'finishing-zone-1': cncLatheExtras,
  'finishing-zone-2': hydraulicPumpExtras,
  'finishing-zone-3': mainDriveMotorExtras,
  // Assembly zone
  'assembly-zone-1': weldingRobotExtras,
  'assembly-zone-2': torqueStationExtras,
  'assembly-zone-3': assemblyConveyorExtras,
  // Packaging zone
  'packaging-zone-1': heatSealerExtras,
  'packaging-zone-2': labelPrinterExtras,
  'packaging-zone-3': palletizerExtras,
  // Legacy alphanumeric IDs (for backward compatibility)
  A231: cncLatheExtras,
  A232: hydraulicPumpExtras,
  A233: mainDriveMotorExtras,
  A234: weldingRobotExtras,
  A235: torqueStationExtras,
  A236: assemblyConveyorExtras,
  A237: heatSealerExtras,
  A238: labelPrinterExtras,
  A239: palletizerExtras,
};

// Provide data for known assets or fallback to CNC Lathe
export const getAssetDetailExtras = (assetId, zoneId) => {
  // Try zone-scoped key first
  if (zoneId) {
    const zoneKey = `${zoneId}-${assetId}`;
    if (assetDetailExtras[zoneKey]) return assetDetailExtras[zoneKey];
  }
  // Try direct key (legacy alphanumeric or numeric string)
  if (assetDetailExtras[assetId]) return assetDetailExtras[assetId];
  // Fallback to CNC Lathe (finishing-zone asset 1)
  return cncLatheExtras;
};
